import { AnimatePresence } from 'framer-motion';
import { useStyletron, Tag, ProgressBar, Text4 } from '@olxeu-jobs/ui';
import { useCallback, useEffect } from 'react';
import { useStableCallback } from '@olxpwa-lib/utils';
import { FormattedMessage } from '~i18n';
import { Platform } from '~types';
import { CheckedCircleFilled } from '~ui/illustrations';
import type { AssistantStep } from '../types';
import {
  Wrapper,
  Header,
  TextContent,
  Actions,
  Title,
  Description,
  IndicatorMotion,
  CountableIndicator,
  ContentMotion,
} from './styled-components';

type Props = AssistantStep & {
  stepNumber: number;
  totalCount: number;
};

export const Step = ({
  id,
  title,
  description,
  icon,
  isNew,
  actions,
  completed,
  dimWhenCompleted,
  countable,
  stepNumber,
  totalCount,
  onMount: MAYBE_UNSTABLE_onMount,
}: Props) => {
  const [, theme] = useStyletron();
  const onMount = useStableCallback(() => MAYBE_UNSTABLE_onMount?.());
  const isDesktop = theme.platform === Platform.DESKTOP;

  const ProgressBarText = useCallback(
    () => (
      <Text4 $variant="bold" $style={{ letterSpacing: '1px' }}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {`${stepNumber}/${totalCount}`}
      </Text4>
    ),
    [stepNumber, totalCount],
  );

  const renderIndicator = () => {
    let indicator: React.ReactNode;

    if (countable) {
      indicator = (
        <CountableIndicator>
          {completed ? (
            <CheckedCircleFilled />
          ) : (
            <ProgressBar
              variant="circular"
              value={stepNumber - 1}
              maxValue={totalCount}
              overrides={{
                Text: {
                  component: ProgressBarText,
                },
              }}
            />
          )}
        </CountableIndicator>
      );
    }

    if (isNew && !completed) {
      indicator = (
        <Tag
          variant="highlight"
          content={<FormattedMessage id="comp.tag.new" defaultMessage="New" />}
        />
      );
    }

    if (icon) {
      indicator = icon;
    }

    if (!indicator) {
      return null;
    }

    return (
      <AnimatePresence initial={!!indicator && completed} mode="wait">
        <IndicatorMotion
          key={`${id}${completed}`}
          layout
          transition={{ duration: 0.2 }}
          initial={{ scale: 0.5, opacity: 0, rotate: -180, width: '48px' }}
          animate={{ scale: 1, opacity: 1, rotate: 0, width: isNew ? 'auto' : '48px' }}
          exit={{ scale: 0.5, opacity: 0, rotate: 180, width: '48px' }}
        >
          {indicator}
        </IndicatorMotion>
      </AnimatePresence>
    );
  };

  useEffect(() => {
    if (id) {
      onMount();
    }
  }, [id, onMount]);

  return (
    <Wrapper>
      {isDesktop && renderIndicator()}
      <AnimatePresence initial={false} mode="popLayout">
        <ContentMotion
          key={id}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
        >
          <TextContent>
            <Header>
              {!isDesktop && renderIndicator()}
              <Title $dim={completed && dimWhenCompleted}>{title}</Title>
            </Header>
            {description && (
              <Description $dim={completed && dimWhenCompleted}>{description}</Description>
            )}
          </TextContent>
          {actions && <Actions>{actions}</Actions>}
        </ContentMotion>
      </AnimatePresence>
    </Wrapper>
  );
};
